/// <amd-module name="Build/PipelinesLanguage/AzurePipelinesLanguageExtension" />
import { getLanguageService, JSONSchema, YAMLDocument } from "AzurePipelinesLanguageService";
import { CodeLensOptions } from "Build/Common/Library/Contracts";
import { IMonacoLanguageRegistrationService } from "Services/MonacoLanguageRegistrationService";
import { SchemaService } from "Services/SchemaService";
import { ValidationService } from "Services/ValidationService";
import * as LanguageProviders from "Utils/LanguageProviders";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { languageExtentionPoint, pipelinesModelUri } from "./Constants";
import { yamlParse } from "./Utils/Yaml";

function isTextModel(model: any): model is monaco.editor.ITextModel {
    return model && model.id && model.uri;
}

export class AzurePipelinesLanguageExtension {
    private lastParsedText: string;
    private lastParsedYamlDocument: YAMLDocument;

    public static register(
        pageContext: IVssPageContext,
        editor: monaco.editor.IStandaloneCodeEditor,
        languageService: IMonacoLanguageRegistrationService,
        codeLensOptions?: CodeLensOptions
    ): AzurePipelinesLanguageExtension | undefined {
        const model = editor.getModel();
        if (!isTextModel(model)) {
            return undefined;
        }
        const extension = new AzurePipelinesLanguageExtension(pageContext, editor, model, languageService, codeLensOptions);
        extension.register();
        return extension;
    }

    private _pageContext: IVssPageContext;
    private _editor: monaco.editor.IStandaloneCodeEditor;
    private _model: monaco.editor.ITextModel;
    private _registrations: monaco.IDisposable[];
    private languageService?: LanguageProviders.AzurePipelinesLanguageService;
    private registerService: IMonacoLanguageRegistrationService;
    private schemaService: SchemaService;
    private _codeLensOptions?: CodeLensOptions;

    protected constructor(
        pageContext: IVssPageContext,
        editor: monaco.editor.IStandaloneCodeEditor,
        model: monaco.editor.ITextModel,
        registerService: IMonacoLanguageRegistrationService,
        codeLensOptions?: CodeLensOptions
    ) {
        this._pageContext = pageContext;
        this._model = model;
        this._registrations = [];
        this.registerService = registerService;
        this._editor = editor;
        this._codeLensOptions = codeLensOptions;

        this.schemaService = new SchemaService(pageContext);
    }

    public dispose(): void {
        if (this._registrations) {
            this._registrations.forEach(registration => registration && registration.dispose());
        }
    }

    public useSchema(schema: JSONSchema) {
        this.schemaService.useSchema(pipelinesModelUri, schema);
        this.languageService?.configure( { validate: true });
    }

    protected register(): void {
        this.languageService = createLanguageService(this.schemaService, this.parse);

        const languageId = languageExtentionPoint.id;
        this.registerService.register(languageExtentionPoint);

        const completionProvider = LanguageProviders.completionProvider(this.languageService);
        this._registrations.push(completionProvider);

        const codeLensFFEnabled = isFeatureFlagEnabled(this._pageContext, "Build2.YamlCodelens", false);
        if (codeLensFFEnabled && this._codeLensOptions && this._editor.addCommand) {
            const codeLensProvider = LanguageProviders.codeLensProvider(this.languageService, this._pageContext, this._editor, this._codeLensOptions);
            this._registrations.push(codeLensProvider);
            this._registrations.push(this.registerService.registerCodeLensProvider(languageId, codeLensProvider));
        }

        this._registrations.push(
            this.registerService.registerCompletionItemProvider(languageId, completionProvider),
            this.registerService.registerDocumentSymbolProvider(languageId, LanguageProviders.documentSymbolProvider(this.languageService)),
            this.registerService.registerHoverProvider(languageId, LanguageProviders.hoverProvider(this.languageService))
        );

        const validationService = new ValidationService(this._model, this.languageService);
        this._registrations.push(this._model.onDidChangeContent(validationService.validate));
    }

    private parse = (text: string) => {
        if (this.lastParsedText !== text) {
            this.lastParsedYamlDocument = yamlParse(text);
            this.lastParsedText = text;
        }

        return this.lastParsedYamlDocument;
    };
}

export function createLanguageService(schemaService: SchemaService, parse: (text: string) => YAMLDocument) {
    return {
        ...getLanguageService(schemaService.getSchema, [], LanguageProviders.schemaResolver),
        parse
    } as LanguageProviders.AzurePipelinesLanguageService;
}
